import { useCallback, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { getMcpAccessAuditApi, McpToolInvocation } from '../Api/McpAccess';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

export interface UseMcpAccessAuditReturn {
  invocations: McpToolInvocation[];
  page: number;
  pageSize: number;
  totalCount: number;
  loading: boolean;
  error: string | null;
  setPaginationModel: (page: number, pageSize: number) => void;
  reload: () => void;
}

const invocationsSubject = new BehaviorSubject<McpToolInvocation[]>([]);
const loadingSubject = new BehaviorSubject<boolean>(false);
const errorSubject = new BehaviorSubject<string | null>(null);
const pageSubject = new BehaviorSubject<number>(DEFAULT_PAGE);
const pageSizeSubject = new BehaviorSubject<number>(DEFAULT_PAGE_SIZE);
const totalCountSubject = new BehaviorSubject<number>(0);

// Monotonic token so only the latest request applies its result. Pagination changes and profile
// switches fire overlapping loads; reset bumps it too, so an in-flight load that resolves after the
// drawer closes is discarded instead of repopulating the store.
let requestSeq = 0;

export const getMcpAccessAuditInvocations = (): McpToolInvocation[] => invocationsSubject.getValue();
export const getMcpAccessAuditLoading = (): boolean => loadingSubject.getValue();
export const getMcpAccessAuditError = (): string | null => errorSubject.getValue();
export const getMcpAccessAuditPage = (): number => pageSubject.getValue();
export const getMcpAccessAuditPageSize = (): number => pageSizeSubject.getValue();
export const getMcpAccessAuditTotalCount = (): number => totalCountSubject.getValue();

export const loadMcpAccessAudit = async (
  accessId: string,
  page = DEFAULT_PAGE,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<void> => {
  const seq = ++requestSeq;
  loadingSubject.next(true);
  errorSubject.next(null);
  pageSubject.next(page);
  pageSizeSubject.next(pageSize);
  const result = await getMcpAccessAuditApi(accessId, page, pageSize);
  // A newer load (or a reset) superseded this request; drop its now-stale result.
  if (seq !== requestSeq) return;
  loadingSubject.next(false);
  if (result.error || !result.data) {
    errorSubject.next(result.error?.message || 'Could not load audit history');
    invocationsSubject.next([]);
    totalCountSubject.next(0);
    return;
  }
  invocationsSubject.next(result.data.data);
  totalCountSubject.next(result.data.totalCount);
};

export const resetMcpAccessAudit = (): void => {
  requestSeq++; // invalidate any in-flight load so it can't repopulate the store after reset
  invocationsSubject.next([]);
  loadingSubject.next(false);
  errorSubject.next(null);
  pageSubject.next(DEFAULT_PAGE);
  pageSizeSubject.next(DEFAULT_PAGE_SIZE);
  totalCountSubject.next(0);
};

export const useMcpAccessAudit = (accessId: string | null): UseMcpAccessAuditReturn => {
  const [invocations, setInvocations] = useState<McpToolInvocation[]>(getMcpAccessAuditInvocations());
  const [loading, setLoading] = useState<boolean>(getMcpAccessAuditLoading());
  const [error, setError] = useState<string | null>(getMcpAccessAuditError());
  const [page, setPage] = useState<number>(getMcpAccessAuditPage());
  const [pageSize, setPageSize] = useState<number>(getMcpAccessAuditPageSize());
  const [totalCount, setTotalCount] = useState<number>(getMcpAccessAuditTotalCount());

  useEffect(() => {
    const subs = [
      invocationsSubject.subscribe(setInvocations),
      loadingSubject.subscribe(setLoading),
      errorSubject.subscribe(setError),
      pageSubject.subscribe(setPage),
      pageSizeSubject.subscribe(setPageSize),
      totalCountSubject.subscribe(setTotalCount)
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  // A newly selected profile starts its history at page 1; clearing it resets the store.
  useEffect(() => {
    if (!accessId) {
      resetMcpAccessAudit();
      return;
    }
    loadMcpAccessAudit(accessId, DEFAULT_PAGE, DEFAULT_PAGE_SIZE);
  }, [accessId]);

  const setPaginationModel = useCallback(
    (nextPage: number, nextPageSize: number) => {
      if (!accessId) return;
      loadMcpAccessAudit(accessId, nextPage, nextPageSize);
    },
    [accessId]
  );

  const reload = useCallback(() => {
    if (!accessId) return;
    loadMcpAccessAudit(accessId, getMcpAccessAuditPage(), getMcpAccessAuditPageSize());
  }, [accessId]);

  return { invocations, page, pageSize, totalCount, loading, error, setPaginationModel, reload };
};
